import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Modal from "../../../../../components/Modal";
import Button from "../../../../../components/Button";
import Spinner from "../../../../../components/Spinner";
import Badge from "../../../../../components/Badge";
import ManagerStatementService from "../../../../../services/managerStatement.service";
import { PropertyVisitBifurcation } from "../../../../../interfaces/managerStatement.interface";
import { formatAmount, formatDollarAmount } from "../../../../../utils/methods";
import { DISTANCE_FROM_LOCATION } from "../../../../../utils/appConstant";

interface PropertyVisitBifurcationModalProps {
  open: boolean;
  statementId: string;
  invoiceId: number | null;
  onClose: () => void;
}

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'danger' | 'warning' | 'secondary'> = {
  completed: 'success',
  not_qualified: 'danger',
  needs_clarification: 'warning',
  to_be_reviewed: 'secondary',
};

const DISTANCE_NOTE_VARIANT: Record<string, 'success' | 'warning' | 'danger'> = {
  [DISTANCE_FROM_LOCATION.le50Km]: 'danger',
  [DISTANCE_FROM_LOCATION.between50And100Km]: 'warning',
  [DISTANCE_FROM_LOCATION.gt100KmGreen]: 'success',
};

const DISTANCE_NOTE_ICON: Record<'success' | 'warning' | 'danger', string> = {
  danger: 'ri-error-warning-line',
  warning: 'ri-alert-line',
  success: 'ri-checkbox-circle-line',
};

const PropertyVisitBifurcationModal: React.FC<PropertyVisitBifurcationModalProps> = ({
  open, statementId, invoiceId, onClose,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PropertyVisitBifurcation | null>(null);

  useEffect(() => {
    if (!open || !invoiceId) {
      setData(null);
      return;
    }

    setLoading(true);
    ManagerStatementService.getPropertyVisitBifurcation(statementId, String(invoiceId))
      .then((response: any) => {
        setData(response ?? null);
        setLoading(false);
      })
      .catch(() => {
        setData(null);
        setLoading(false);
      });
  }, [open, invoiceId, statementId]);

  const base = 'reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.bifurcation';

  const content = loading ? (
    <div className="d-flex justify-content-center p-4">
      <Spinner variant="primary" />
    </div>
  ) : data ? (
    <div>
      <div className="mb-4">
        <div className="text-body small fw-semibold">{t(`${base}.distanceLabel`)}</div>
        <div className="fw-semibold">{data.distance_band_name || '-'}</div>
        {data.distance_band && (
          <div
            className={clsx('d-flex align-items-center p-3 mt-2 rounded border', `border-${DISTANCE_NOTE_VARIANT[data.distance_band]}`)}
            role="alert"
          >
            <i className={clsx(DISTANCE_NOTE_ICON[DISTANCE_NOTE_VARIANT[data.distance_band]], 'fs-3 me-3', `text-${DISTANCE_NOTE_VARIANT[data.distance_band]}`)}></i>
            <span className="text-body">{t(`${base}.policyNotes.${data.distance_band}`)}</span>
          </div>
        )}
      </div>

      <div className="bg-body rounded p-3 mb-4">
        <div className="d-flex justify-content-between">
          <span>{t(`${base}.breakdown.visitCommission`)}</span>
          <span className="fw-semibold">{formatDollarAmount(formatAmount(data.property_visit_commission.toFixed(2)))}</span>
        </div>
        <div className="d-flex justify-content-between mt-2">
          <span>{t(`${base}.breakdown.convenienceFee`)}</span>
          <span className="fw-semibold">{formatDollarAmount(formatAmount(data.convenience_fee.toFixed(2)))}</span>
        </div>
        <div className="d-flex justify-content-between fw-bold border-top pt-2 mt-2">
          <span>{t(`${base}.breakdown.total`)}</span>
          <span>{formatDollarAmount(formatAmount(data.total_property_visit_commission.toFixed(2)))}</span>
        </div>
      </div>

      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-bold mb-0 d-flex align-items-center">
            <i className="ri-calendar-check-line ri-18px me-2"></i>
            {t(`${base}.visits.title`)}
          </h6>
          <span className={data.minimum_visit_met ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
            {data.expected_visits === 0
              ? t(`${base}.summary.notRequired`)
              : t(`${base}.summary.completedOfExpected`, { completed: data.completed_visits_count, expected: data.expected_visits })}
          </span>
        </div>

        {data.visits.length === 0 ? (
          <p className="text-body mb-0">{t(`${base}.visits.empty`)}</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t(`${base}.visits.columns.date`)}</th>
                  <th>{t(`${base}.visits.columns.visitType`)}</th>
                  <th>{t(`${base}.visits.columns.visitedBy`)}</th>
                  <th>{t(`${base}.visits.columns.status`)}</th>
                </tr>
              </thead>
              <tbody>
                {data.visits.map((visit) => (
                  <tr key={visit.id}>
                    <td>{visit.visit_date || '-'}</td>
                    <td>{visit.visit_type_name || '-'}</td>
                    <td>{visit.visited_by_name || '-'} {visit.user_role_name ? `(${visit.user_role_name})` : ''}</td>
                    <td>
                      <Badge text={visit.status_name || visit.status} variant={STATUS_BADGE_VARIANT[visit.status] || 'secondary'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  ) : null;

  const subtitle = data
    ? [data.invoice_no, data.invoice_month_name && data.invoice_year ? `${data.invoice_month_name}-${data.invoice_year}` : null, data.property_name].filter(Boolean).join(' · ')
    : '';

  const modalTitle = (
    <div className="d-flex justify-content-between align-items-start w-100">
      <div>
        <h5 className="fw-bold mb-0">{t(`${base}.title`)}</h5>
        {subtitle && <div className="text-body small fw-semibold">{subtitle}</div>}
      </div>
      <Button
        className="btn btn-sm btn-icon btn-text-secondary"
        icon={<i className="ri-close-line ri-20px" />}
        onClick={onClose}
      />
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={modalTitle}
      titleClassName="text-start"
      actionClassName="justify-content-start"
      content={content}
      action={(
        <Button
          className="btn btn-outline-secondary"
          text={t(`${base}.close`)}
          onClick={onClose}
        />
      )}
    />
  );
};

export default PropertyVisitBifurcationModal;
